import { t } from "./i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, EasyOrdersApi } from "./api";
import type {
  CompletedLoginResponse,
  MfaChallengeResponse,
  PrintResponse
} from "./api";
import { safeDiagnosticError } from "./diagnosticSchema";
import { diagnostics } from "./diagnostics";
import { resolveOrderPrintRouting } from "./printRouting";
import {
  buildOrderPayloads,
  buildSalesReportPayloads,
  buildTestPayloads,
  tableChangePrinters
} from "./ticketRenderer";
import {
  applyFontFamily,
  applySyncedPreferences,
  applyTextPrefs,
  applyThemeColor,
  collectSyncedPreferences,
  deviceId,
  deviceName,
  loadDraft,
  loadFontFamily,
  loadOrderSoundEnabled,
  loadOrders,
  loadPending,
  loadRememberPreference,
  loadSession,
  loadSetup,
  loadTextBold,
  loadTextSizePercent,
  loadThemeColor,
  saveDraft,
  saveOrders,
  savePending,
  saveSession,
  saveSetup,
  watchPreferenceChanges
} from "./storage";
import type {
  DraftLine,
  OrderRecord,
  Orders,
  PendingSubmission,
  PrinterConfig,
  PrintTransport,
  SalesPeriodReport,
  Session,
  Setup,
  SetupCollectionKey,
  SetupPatch
} from "./types";
import {
  counterText,
  isOpenOrder,
  isOrderRecorded,
  pendingForTable,
  pendingOrderRecord,
  pendingTableLabel,
  selectedExtrasValue,
  uuid
} from "./utils";

export type ConnectionState = "connecting" | "online" | "offline";

type LoginInput = {
  businessSlug: string;
  username: string;
  password: string;
  remember?: boolean;
};

export type PendingMfa = MfaChallengeResponse & {
  businessSlug: string;
  remember: boolean;
  actionId: string;
};

const SETUP_COLLECTIONS: SetupCollectionKey[] = [
  "users",
  "categories",
  "products",
  "shop_tables",
  "table_pages",
  "printers",
  "settings"
];

function setupPatchBetween(previous: Setup, next: Setup): SetupPatch {
  const patch: SetupPatch = { upserts: {}, deletes: {} };
  for (const collection of SETUP_COLLECTIONS) {
    const keyName = collection === "settings" ? "key" : "id";
    const previousRows = previous[collection] as unknown as Array<Record<string, unknown>>;
    const nextRows = next[collection] as unknown as Array<Record<string, unknown>>;
    const previousByKey = new Map(previousRows.map((row) => [String(row[keyName]), row]));
    const nextByKey = new Map(nextRows.map((row) => [String(row[keyName]), row]));
    const upserts = nextRows.filter((row) => {
      const oldRow = previousByKey.get(String(row[keyName]));
      return !oldRow || JSON.stringify(oldRow) !== JSON.stringify(row);
    });
    const deletes = previousRows
      .filter((row) => !nextByKey.has(String(row[keyName])))
      .map((row) => row[keyName] as number | string);
    if (upserts.length) {
      patch.upserts[collection] = upserts;
    }
    if (deletes.length) {
      patch.deletes[collection] = deletes;
    }
  }
  return patch;
}

function hasSetupPatchChanges(patch: SetupPatch): boolean {
  return Object.values(patch.upserts).some((rows) => Boolean(rows?.length)) ||
    Object.values(patch.deletes).some((keys) => Boolean(keys?.length));
}

function eventFingerprint(orders: Orders | null): string {
  if (!orders?.order_events.length) {
    return "";
  }
  return orders.order_events
    .map((event) => `${event.id}:${event.order_id}:${event.created_at}:${event.type}`)
    .sort()
    .join("|");
}

/* A pending submission only lives as long as its order does. Once the order is closed, or the
   history has been cleared out from under it, there is nothing left to retry — and leaving the
   entry behind keeps the table tile coloured and the retry loop spinning for good.

   A closed order is safe to act on immediately because we can see the row. A missing row is
   ambiguous: it also describes an orders payload fetched just before the order was created, so
   that case waits until the entry is old enough that no submission could still be in flight. */
const STRANDED_PENDING_AGE_MS = 60_000;

function livePending(pending: PendingSubmission[], orders: Orders): PendingSubmission[] {
  const now = Date.now();
  return pending.filter((item) => {
    const record = pendingOrderRecord(item, orders);
    if (record) {
      return isOpenOrder(record);
    }
    return item.orderId <= 0 || now - item.createdAt <= STRANDED_PENDING_AGE_MS;
  });
}

function latestEvent(orders: Orders | null) {
  return orders?.order_events.reduce(
    (latest, event) => (!latest || event.created_at > latest.created_at ? event : latest),
    orders.order_events[0]
  );
}

export function playOrderSound(): void {
  try {
    const AudioContextClass = window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) {
      return;
    }
    const context = new AudioContextClass();
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.22, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.42);
    gain.connect(context.destination);
    [880, 1174].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      oscillator.connect(gain);
      oscillator.start(context.currentTime + index * 0.12);
      oscillator.stop(context.currentTime + 0.3 + index * 0.12);
    });
    window.setTimeout(() => void context.close(), 700);
  } catch {
    // Sound is best-effort; iOS may require a prior user gesture.
  }
}

function notifyOrderUpdate(orders: Orders): void {
  const event = latestEvent(orders);
  if (!event) {
    return;
  }
  const order = orders.orders.find((candidate) => candidate.id === event.order_id);
  const title = order
    ? t("Τραπέζι {0} · #{1}", order.table_label, order.order_number)
    : t("Ενημέρωση παραγγελίας");
  if (loadOrderSoundEnabled()) {
    playOrderSound();
  }
  if ("Notification" in window && Notification.permission === "granted"
      && document.visibilityState !== "visible") {
    const options: NotificationOptions = {
      body: event.type,
      icon: "/icons/icon-192.png",
      badge: "/icons/icon-192.png",
      tag: `easyorders-event-${event.id}`
    };
    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker.ready.then((registration) =>
        registration.showNotification(title, options)
      );
    } else {
      const notification = new Notification(title, options);
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  }
}

export function useEasyOrders() {
  const initialSession = useMemo(() => loadSession(), []);
  const [session, setSessionState] = useState<Session | null>(initialSession);
  const [setup, setSetupState] = useState<Setup | null>(
    initialSession ? loadSetup(initialSession) : null
  );
  const [orders, setOrdersState] = useState<Orders | null>(
    initialSession ? loadOrders(initialSession) : null
  );
  const [pending, setPendingState] = useState<PendingSubmission[]>(
    initialSession ? loadPending(initialSession) : []
  );
  const [drafts, setDrafts] = useState<Record<string, DraftLine[]>>({});
  const [connection, setConnection] = useState<ConnectionState>(
    navigator.onLine ? "connecting" : "offline"
  );
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(0);
  const [message, setMessage] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState<PendingMfa | null>(null);
  const [mfaRecoveryCodes, setMfaRecoveryCodes] = useState<string[]>([]);
  const [actionBusy, setActionBusy] = useState(false);
  const [health, setHealth] = useState<{
    schemaVersion: number;
    apiVersion: number;
    deploymentMode: string;
  } | null>(null);
  /* Construct exactly once. Passing `new EasyOrdersApi(...)` directly to useRef evaluates it on
     every render and would repeatedly clear diagnostics auth after login. */
  const apiRef = useRef<EasyOrdersApi>(null!);
  if (!apiRef.current) {
    apiRef.current = new EasyOrdersApi(initialSession);
  }
  const sessionRef = useRef(session);
  const rememberRef = useRef(loadRememberPreference());
  /* Table moves made while the order was still a draft. Cleared once they are reported. */
  const draftTableChangesRef = useRef<Array<{
    source_table_label: string;
    target_table_label: string;
    created_at: number;
  }>>([]);
  const setupRef = useRef(setup);
  const committedSetupRef = useRef(setup);
  const setupMutationQueueRef = useRef<Promise<void>>(Promise.resolve());
  const setupMutationGenerationRef = useRef(0);
  const setupMutationPendingRef = useRef(0);
  const ordersRef = useRef(orders);
  const pendingRef = useRef(pending);
  const draftsRef = useRef<Record<string, DraftLine[]>>({});
  const syncBusyRef = useRef(false);
  const processBusyRef = useRef(false);
  const historyPrintBusyRef = useRef(new Set<number>());
  const initialOrdersLoadedRef = useRef(Boolean(orders));
  const previousEventFingerprintRef = useRef(eventFingerprint(orders));

  const persistSession = useCallback((next: Session | null, remember = rememberRef.current) => {
    sessionRef.current = next;
    apiRef.current.setSession(next);
    rememberRef.current = remember;
    saveSession(next, remember);
    setSessionState(next);
  }, []);

  const persistSetup = useCallback((next: Setup) => {
    const current = sessionRef.current;
    setupRef.current = next;
    setSetupState(next);
    if (current) {
      saveSetup(current, next);
    }
  }, []);

  const acceptServerSetup = useCallback((next: Setup) => {
    committedSetupRef.current = next;
    persistSetup(next);
  }, [persistSetup]);

  /**
   * Queues a ticket, rendering it here when possible.
   *
   * Rendering locally lets the server print over the tunnel from any network, with no Android
   * device on the shop LAN. The job still carries order_id/event_id, so when the server cannot
   * reach the printers an on-site relay can claim and render it exactly as before.
   */
  const queuePrintJob = useCallback(async (input: {
    printJobId: string;
    submissionId?: string;
    orderId: number;
    eventId: number;
    profile: "order" | "history";
    printerIds: number[];
    /* The transport a previous attempt on this job id already used, if any. */
    transport?: PrintTransport;
    routeMode?: "all" | "automatic" | "manual";
    routing?: Record<string, number[]>;
  }, actionId?: string): Promise<{ response: PrintResponse; transport: PrintTransport }> => {
    if (input.transport !== "order") {
      const payloads = await buildOrderPayloads(setupRef.current, ordersRef.current, {
        orderId: input.orderId,
        eventId: input.eventId,
        profile: input.profile,
        printerIds: input.printerIds,
        routing: input.routing
      });
      if (payloads) {
        const response = await apiRef.current.rawPrint({
          printJobId: input.printJobId,
          submissionId: input.submissionId,
          orderId: input.orderId,
          eventId: input.eventId,
          printerPayloads: payloads,
          routeMode: input.routeMode,
          routing: input.routing
        }, actionId);
        return { response, transport: "raw" };
      }
      if (input.transport === "raw") {
        /* This job already exists on the server holding a raster's hash. Queueing it by id now
           would be refused for good, so fail this attempt instead and let it be retried once the
           order is back in the local cache. */
        throw new Error(t("Δεν ήταν δυνατή η προετοιμασία του δελτίου. Δοκίμασε ξανά."));
      }
    }
    const response = await apiRef.current.queuePrint({
      printJobId: input.printJobId,
      submissionId: input.submissionId,
      orderId: input.orderId,
      eventId: input.eventId,
      profile: input.profile,
      printerIds: input.printerIds,
      routeMode: input.routeMode,
      routing: input.routing
    }, actionId);
    return { response, transport: "order" };
  }, []);

  const persistOrders = useCallback((next: Orders, notify = true) => {
    const previousFingerprint = previousEventFingerprintRef.current;
    const nextFingerprint = eventFingerprint(next);
    ordersRef.current = next;
    setOrdersState(next);
    const current = sessionRef.current;
    if (current) {
      saveOrders(current, next);
    }
    const reboundPending = livePending(pendingRef.current, next).map((item) => {
      const effectiveLabel = pendingTableLabel(item, next);
      return effectiveLabel === item.tableLabel ? item : { ...item, tableLabel: effectiveLabel };
    });
    if (
      reboundPending.length !== pendingRef.current.length ||
      reboundPending.some((item, index) => item !== pendingRef.current[index])
    ) {
      pendingRef.current = reboundPending;
      setPendingState(reboundPending);
      if (current) {
        savePending(current, reboundPending);
      }
    }
    if (
      notify &&
      initialOrdersLoadedRef.current &&
      previousFingerprint &&
      previousFingerprint !== nextFingerprint
    ) {
      notifyOrderUpdate(next);
    }
    initialOrdersLoadedRef.current = true;
    previousEventFingerprintRef.current = nextFingerprint;
  }, []);

  const persistPending = useCallback((next: PendingSubmission[]) => {
    pendingRef.current = next;
    setPendingState(next);
    const current = sessionRef.current;
    if (current) {
      savePending(current, next);
    }
  }, []);

  const invalidateSession = useCallback((error?: ApiError) => {
    if (error) {
      diagnostics.record({
        name: "auth.session",
        category: "AUTH",
        severity: "WARNING",
        outcome: "failed",
        metadata: { operation: "invalidated", ...safeDiagnosticError(error) }
      });
    }
    persistSession(null);
    setSetupState(null);
    setupRef.current = null;
    committedSetupRef.current = null;
    setOrdersState(null);
    setPendingState([]);
    setDrafts({});
    setConnection("offline");
    setMessage(
      error?.code === "session_replaced"
        ? t("Ο λογαριασμός συνδέθηκε από άλλη συσκευή.")
        : error?.message || t("Η συνεδρία έληξε. Συνδεθείτε ξανά.")
    );
  }, [persistSession]);

  useEffect(() => {
    apiRef.current.onSessionInvalid = invalidateSession;
  }, [invalidateSession]);

  /* Every settings screen writes through localStorage, so one watcher catches all of them —
     including any added later — instead of a push call at each site. Debounced inside the
     watcher, since changing one control often writes several keys. */
  useEffect(() => {
    return watchPreferenceChanges(() => {
      if (!sessionRef.current) {
        return;
      }
      void apiRef.current.savePreferences(collectSyncedPreferences()).catch(() => {
        // Preferences are a convenience; the local copy is authoritative until this succeeds.
      });
    });
  }, []);

  const updatePending = useCallback(
    (submissionId: string, patch: Partial<PendingSubmission>) => {
      const next = pendingRef.current.map((item) =>
        item.submissionId === submissionId ? { ...item, ...patch } : item
      );
      persistPending(next);
      return next.find((item) => item.submissionId === submissionId) ?? null;
    },
    [persistPending]
  );

  const removePending = useCallback((submissionId: string) => {
    persistPending(pendingRef.current.filter((item) => item.submissionId !== submissionId));
  }, [persistPending]);

  const refreshOrders = useCallback(async (notify = true, actionId?: string) => {
    const next = await apiRef.current.orders(actionId);
    persistOrders(next, notify);
    return next;
  }, [persistOrders]);

  const processSubmission = useCallback(async (
    submissionId: string,
    explicitRetry = false,
    actionId?: string
  ) => {
    if (processBusyRef.current || !sessionRef.current) {
      return;
    }
    const pendingItem = pendingRef.current.find((item) => item.submissionId === submissionId);
    if (!pendingItem || (pendingItem.state === "unknown" && !explicitRetry)) {
      return;
    }
    const correlationActionId = actionId || pendingItem.actionId || diagnostics.newActionId();
    if (pendingItem.actionId !== correlationActionId) {
      updatePending(submissionId, { actionId: correlationActionId });
    }
    processBusyRef.current = true;
    try {
      let active = pendingItem;
      if (active.state === "unknown") {
        const response = await apiRef.current.printStatus(active.printJobId, correlationActionId);
        const status = response.job?.status ?? "unknown";
        if (status === "sent" || status === "sent_direct") {
          removePending(active.submissionId);
          diagnostics.record({
            name: "order.print",
            category: "USER_ACTION",
            outcome: "succeeded",
            actionId: correlationActionId,
            entityType: "order",
            entityId: active.orderId,
            metadata: {
              operation: "status_check",
              submission_id: active.submissionId,
              print_job_id: active.printJobId,
              result: status
            }
          });
          setMessage(t("Η εκτύπωση επιβεβαιώθηκε."));
        } else if (status === "failed") {
          updatePending(active.submissionId, {
            state: "failed",
            error: response.job?.last_error || t("Η εκτύπωση απέτυχε.")
          });
          diagnostics.record({
            name: "order.print",
            category: "USER_ACTION",
            severity: "ERROR",
            outcome: "failed",
            actionId: correlationActionId,
            entityType: "order",
            entityId: active.orderId,
            metadata: {
              operation: "status_check",
              submission_id: active.submissionId,
              print_job_id: active.printJobId,
              result: status,
              error_code: "print_failed"
            }
          });
          setMessage(t("Η εκτύπωση απέτυχε. Μπορείτε να την επαναλάβετε."));
        } else if (["pending", "pending_client", "claimed", "printing"].includes(status)) {
          updatePending(active.submissionId, { state: "printing", error: "" });
          setMessage(t("Η εκτύπωση βρίσκεται ακόμη σε εξέλιξη."));
        } else {
          updatePending(active.submissionId, {
            state: "unknown",
            error: response.job?.last_error || t("Το αποτέλεσμα της εκτύπωσης παραμένει άγνωστο.")
          });
          diagnostics.record({
            name: "order.print",
            category: "USER_ACTION",
            severity: "WARNING",
            outcome: "unknown",
            actionId: correlationActionId,
            entityType: "order",
            entityId: active.orderId,
            metadata: {
              operation: "status_check",
              submission_id: active.submissionId,
              print_job_id: active.printJobId,
              result: "unknown"
            }
          });
          setMessage(t("Το αποτέλεσμα της εκτύπωσης παραμένει άγνωστο."));
        }
        setConnection("online");
        return;
      }
      if (active.state === "order_pending") {
        const response = await apiRef.current.createOrder(active.payload, correlationActionId);
        active = updatePending(active.submissionId, {
          state: "print_pending",
          orderId: response.order_id,
          eventId: response.event_id,
          error: ""
        }) ?? active;
        diagnostics.record({
          name: "order.submit",
          category: "USER_ACTION",
          outcome: response.deduplicated ? "deduplicated" : "succeeded",
          actionId: correlationActionId,
          entityType: "order",
          entityId: response.order_id,
          metadata: {
            operation: "create",
            submission_id: active.submissionId,
            deduplicated: response.deduplicated
          }
        });
        const currentSession = sessionRef.current;
        if (currentSession) {
          saveDraft(currentSession, active.tableLabel, []);
          draftsRef.current = { ...draftsRef.current, [active.tableLabel]: [] };
          setDrafts((current) => ({ ...current, [active.tableLabel]: [] }));
        }
        await refreshOrders(false, correlationActionId);
      }

      if (active.state === "print_pending" || (active.state === "failed" && explicitRetry)) {
        const attempt = await queuePrintJob({
          printJobId: active.printJobId,
          submissionId: active.submissionId,
          orderId: active.orderId,
          eventId: active.eventId,
          profile: "order",
          printerIds: active.printerIds,
          routeMode: active.routeMode,
          routing: active.routing,
          transport: active.printTransport
        }, correlationActionId);
        const queued = attempt.response;
        if (active.printTransport !== attempt.transport) {
          active = updatePending(active.submissionId, {
            printTransport: attempt.transport
          }) ?? active;
        }
        if (!queued.client_delivery_required || ["sent", "sent_direct"].includes(queued.job.status)) {
          removePending(active.submissionId);
          diagnostics.record({
            name: "order.print",
            category: "USER_ACTION",
            outcome: queued.deduplicated ? "deduplicated" : "succeeded",
            actionId: correlationActionId,
            entityType: "order",
            entityId: active.orderId,
            metadata: {
              operation: "queue",
              submission_id: active.submissionId,
              print_job_id: active.printJobId,
              deduplicated: queued.deduplicated,
              result: queued.job.status
            }
          });
          setMessage(t("Η παραγγελία καταχωρήθηκε και εκτυπώθηκε."));
          return;
        }
        active = updatePending(active.submissionId, {
          state: "printing",
          error: ""
        }) ?? active;
        diagnostics.record({
          name: "order.print",
          category: "USER_ACTION",
          outcome: "started",
          actionId: correlationActionId,
          entityType: "order",
          entityId: active.orderId,
          metadata: {
            operation: "queue",
            submission_id: active.submissionId,
            print_job_id: active.printJobId,
            result: queued.job.status,
            printer_count: active.printerIds.length
          }
        });
        setMessage(t("Η παραγγελία καταχωρήθηκε και περιμένει τον εκτυπωτή."));
      }

      if (active.state === "printing") {
        const response = await apiRef.current.printStatus(active.printJobId, correlationActionId);
        const status = response.job?.status ?? "";
        if (status === "sent" || status === "sent_direct") {
          removePending(active.submissionId);
          diagnostics.record({
            name: "order.print",
            category: "USER_ACTION",
            outcome: "succeeded",
            actionId: correlationActionId,
            entityType: "order",
            entityId: active.orderId,
            metadata: {
              operation: "delivery",
              submission_id: active.submissionId,
              print_job_id: active.printJobId,
              result: status
            }
          });
          setMessage(t("Η παραγγελία εκτυπώθηκε."));
        } else if (status === "failed") {
          updatePending(active.submissionId, {
            state: "failed",
            error: response.job?.last_error || t("Η εκτύπωση απέτυχε.")
          });
          diagnostics.record({
            name: "order.print",
            category: "USER_ACTION",
            severity: "ERROR",
            outcome: "failed",
            actionId: correlationActionId,
            entityType: "order",
            entityId: active.orderId,
            metadata: {
              operation: "delivery",
              submission_id: active.submissionId,
              print_job_id: active.printJobId,
              result: status,
              error_code: "print_failed"
            }
          });
        } else if (status === "unknown") {
          updatePending(active.submissionId, {
            state: "unknown",
            error: response.job?.last_error || t("Το αποτέλεσμα της εκτύπωσης είναι άγνωστο.")
          });
          diagnostics.record({
            name: "order.print",
            category: "USER_ACTION",
            severity: "WARNING",
            outcome: "unknown",
            actionId: correlationActionId,
            entityType: "order",
            entityId: active.orderId,
            metadata: {
              operation: "delivery",
              submission_id: active.submissionId,
              print_job_id: active.printJobId,
              result: status
            }
          });
        }
      }
      setConnection("online");
    } catch (error) {
      setConnection(navigator.onLine ? "offline" : "offline");
      const text = error instanceof Error ? error.message : t("Άγνωστο σφάλμα");
      const current = pendingRef.current.find((item) => item.submissionId === submissionId);
      if (error instanceof ApiError && error.code === "print_outcome_unknown") {
        updatePending(submissionId, { state: "unknown", error: text });
      } else if (current?.orderId) {
        updatePending(submissionId, {
          state: error instanceof ApiError ? "failed" : current.state,
          error: text
        });
      } else {
        updatePending(submissionId, { error: text });
      }
      setMessage(text);
      diagnostics.record({
        name: current?.orderId ? "order.print" : "order.submit",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId: correlationActionId,
        entityType: current?.orderId ? "order" : undefined,
        entityId: current?.orderId || undefined,
        metadata: {
          operation: explicitRetry ? "retry" : "process",
          submission_id: submissionId,
          print_job_id: current?.printJobId,
          pending_state: current?.state,
          online: navigator.onLine,
          ...safeDiagnosticError(error)
        }
      });
    } finally {
      processBusyRef.current = false;
    }
  }, [refreshOrders, removePending, updatePending]);

  const flushPending = useCallback(async () => {
    if (processBusyRef.current) {
      return;
    }
    const next = pendingRef.current.find((item) =>
      ["order_pending", "print_pending", "printing"].includes(item.state)
    );
    if (next) {
      await processSubmission(next.submissionId);
    }
  }, [processSubmission]);

  const sync = useCallback(async (force = false, userInitiated = false) => {
    if (!sessionRef.current || syncBusyRef.current) {
      return;
    }
    const actionId = userInitiated ? diagnostics.beginAction({
      name: "sync.cycle",
      category: "USER_ACTION",
      metadata: { operation: "manual", forced: force }
    }) : undefined;
    syncBusyRef.current = true;
    setSyncing(true);
    try {
      const meta = await apiRef.current.meta(actionId);
      if (!meta.active) {
        throw new ApiError(401, {
          error: meta.session?.reason || "invalid_session",
          message: meta.session?.message || t("Η συνεδρία δεν είναι πλέον ενεργή.")
        });
      }
      const currentSession = sessionRef.current;
      const serverDiagnosticSessionId = meta.session?.diagnostic_session_id;
      if (
        currentSession &&
        serverDiagnosticSessionId &&
        currentSession.diagnosticSessionId !== serverDiagnosticSessionId
      ) {
        persistSession({
          ...currentSession,
          diagnosticSessionId: serverDiagnosticSessionId
        });
      }
      const currentSetup = setupRef.current;
      if (
        setupMutationPendingRef.current === 0 &&
        (force || !currentSetup || currentSetup.revision !== meta.setup_revision)
      ) {
        acceptServerSetup(await apiRef.current.setup(actionId));
      }
      const currentOrders = ordersRef.current;
      if (force || !currentOrders || currentOrders.revision !== meta.orders_revision) {
        persistOrders(await apiRef.current.orders(actionId));
      }
      const info = await apiRef.current.health(actionId);
      setHealth({
        schemaVersion: info.schema_version,
        apiVersion: info.api_version,
        deploymentMode: info.deployment_mode
      });
      setConnection("online");
      setLastSyncAt(Date.now());
      await flushPending();
      if (actionId) {
        diagnostics.record({
          name: "sync.cycle",
          category: "USER_ACTION",
          outcome: "succeeded",
          actionId,
          metadata: { operation: "manual", forced: force, online: true }
        });
      }
    } catch (error) {
      // Explicit session failures clear sessionRef through onSessionInvalid. A generic gateway
      // 401 is only a connection failure and must not erase a remembered login.
      if (sessionRef.current) {
        setConnection("offline");
      }
      diagnostics.record({
        name: "sync.cycle",
        category: "NETWORK",
        severity: "WARNING",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        metadata: {
          operation: userInitiated ? "manual" : "poll",
          forced: force,
          online: navigator.onLine,
          ...safeDiagnosticError(error)
        },
        coalesceKey: userInitiated ? undefined : `sync:${navigator.onLine ? "failed" : "offline"}`,
        coalesceMs: 15_000
      });
    } finally {
      syncBusyRef.current = false;
      setSyncing(false);
    }
  }, [acceptServerSetup, flushPending, persistOrders, persistSession]);

  useEffect(() => {
    if (!session) {
      return;
    }
    void sync(true);
    const timer = window.setInterval(() => {
      void sync();
    }, 1800);
    const onOnline = () => void sync(true);
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void sync(true);
      }
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [session, sync]);

  const previousConnectionRef = useRef<ConnectionState>(connection);
  useEffect(() => {
    const previous = previousConnectionRef.current;
    if (connection === previous || connection === "connecting") return;
    diagnostics.record({
      name: "connection.state",
      category: "NETWORK",
      severity: connection === "offline" ? "WARNING" : "INFO",
      outcome: connection,
      metadata: {
        operation: "server_reachability",
        connection,
        previous_connection: previous,
        online: navigator.onLine
      },
      coalesceKey: `server_connection:${connection}`,
      coalesceMs: 5_000
    });
    previousConnectionRef.current = connection;
  }, [connection]);

  const finishLogin = useCallback(async (
    response: CompletedLoginResponse,
    businessSlug: string,
    remember: boolean,
    actionId: string
  ) => {
      const nextSession: Session = {
        businessSlug: response.business?.slug || businessSlug,
        user: response.user,
        business: response.business,
        deviceId: response.device_id,
        deviceName: response.device_name,
        token: response.session_token,
        loginAt: response.login_at,
        diagnosticSessionId: response.diagnostic_session_id
      };
      persistSession(nextSession, remember);
      /* Restore this account's settings before the app renders, so a fresh login — or one after
         being signed out — looks the way the person left it rather than back at defaults. A
         failure here is not worth blocking a login for; the device keeps what it had. */
      try {
        const remote = await apiRef.current.getPreferences();
        const applied = applySyncedPreferences(remote.preferences);
        if (applied) {
          applyTextPrefs(loadTextSizePercent(), loadTextBold());
          applyFontFamily(loadFontFamily());
          applyThemeColor(loadThemeColor());
        }
      } catch {
        // Offline or older server: local preferences stand.
      }
      acceptServerSetup(await apiRef.current.setup(actionId));
      persistOrders(await apiRef.current.orders(actionId), false);
      persistPending(loadPending(nextSession));
      setConnection("online");
      if (response.replaced_existing_session) {
        setMessage(t("Η προηγούμενη σύνδεση στο {0} τερματίστηκε.", response.previous_device_name || t("άλλο τηλέφωνο")));
      }
      diagnostics.record({
        name: "auth.session",
        category: "AUTH",
        outcome: "succeeded",
        actionId,
        metadata: {
          operation: "login",
          replaced_session: response.replaced_existing_session
        }
      });
  }, [acceptServerSetup, persistOrders, persistPending, persistSession]);

  const login = useCallback(async (input: LoginInput) => {
    setLoginBusy(true);
    setMessage("");
    setMfaChallenge(null);
    setMfaRecoveryCodes([]);
    const businessSlug = input.businessSlug.trim();
    const remember = input.remember !== false;
    await diagnostics.prepareLogin();
    const actionId = diagnostics.beginAction({
      name: "auth.session",
      category: "AUTH",
      metadata: { operation: "login" }
    });
    try {
      const response = await apiRef.current.login(
        businessSlug,
        input.username.trim(),
        input.password,
        deviceId(),
        deviceName(),
        actionId
      );
      if (
        response.auth_state === "mfa_required" ||
        response.auth_state === "mfa_enrollment_required"
      ) {
        setMfaChallenge({ ...response, businessSlug: response.business?.slug || businessSlug, remember, actionId });
        diagnostics.record({
          name: "auth.session",
          category: "AUTH",
          outcome: "started",
          actionId,
          metadata: { operation: response.auth_state }
        });
        return;
      }
      await finishLogin(response as CompletedLoginResponse, businessSlug, remember, actionId);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t("Δεν έγινε σύνδεση."));
      diagnostics.record({
        name: "auth.session",
        category: "AUTH",
        severity: "WARNING",
        outcome: "failed",
        actionId,
        metadata: { operation: "login", ...safeDiagnosticError(error) }
      });
      throw error;
    } finally {
      setLoginBusy(false);
    }
  }, [finishLogin]);

  const completeMfa = useCallback(async (code: string) => {
    const challenge = mfaChallenge;
    if (!challenge || loginBusy) {
      return;
    }
    setLoginBusy(true);
    setMessage("");
    try {
      const response = await apiRef.current.completeMfa(
        challenge.businessSlug,
        challenge.challenge_token,
        code,
        challenge.actionId
      );
      setMfaRecoveryCodes(response.recovery_codes ?? []);
      await finishLogin(
        response,
        challenge.businessSlug,
        challenge.remember,
        challenge.actionId
      );
      setMfaChallenge(null);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t("Δεν έγινε επαλήθευση."));
      diagnostics.record({
        name: "auth.session",
        category: "AUTH",
        severity: "WARNING",
        outcome: "failed",
        actionId: challenge.actionId,
        metadata: { operation: "mfa_complete", ...safeDiagnosticError(error) }
      });
      throw error;
    } finally {
      setLoginBusy(false);
    }
  }, [finishLogin, loginBusy, mfaChallenge]);

  const cancelMfa = useCallback(() => {
    setMfaChallenge(null);
    setMfaRecoveryCodes([]);
    setMessage("");
  }, []);

  const acknowledgeRecoveryCodes = useCallback(() => {
    setMfaRecoveryCodes([]);
  }, []);

  const logout = useCallback(async () => {
    const actionId = diagnostics.beginAction({
      name: "auth.session",
      category: "AUTH",
      metadata: { operation: "logout" }
    });
    try {
      // Flush the intent while the server session is still valid. The logout endpoint revokes the
      // token, so a success breadcrumb cannot be uploaded afterward (the server audits success).
      await diagnostics.flush();
      await apiRef.current.logout(actionId);
      diagnostics.record({
        name: "auth.session",
        category: "AUTH",
        outcome: "succeeded",
        actionId,
        metadata: { operation: "logout" }
      });
    } catch {
      // Local logout must remain available while offline.
    }
    invalidateSession();
    setMessage("");
  }, [invalidateSession]);

  const ensureDraft = useCallback((tableLabel: string): DraftLine[] => {
    const existing = drafts[tableLabel];
    if (existing) {
      return existing;
    }
    const current = sessionRef.current;
    const loaded = current ? loadDraft(current, tableLabel) : [];
    draftsRef.current = { ...draftsRef.current, [tableLabel]: loaded };
    setDrafts((value) => ({ ...value, [tableLabel]: loaded }));
    return loaded;
  }, [drafts]);

  const setDraft = useCallback((tableLabel: string, lines: DraftLine[]) => {
    const previous = draftsRef.current[tableLabel] ?? [];
    draftsRef.current = { ...draftsRef.current, [tableLabel]: lines };
    setDrafts((value) => ({ ...value, [tableLabel]: lines }));
    const current = sessionRef.current;
    if (current) {
      saveDraft(current, tableLabel, lines);
    }
    const tableId = setupRef.current?.shop_tables.find((table) => table.label === tableLabel)?.id;
    const added = lines.find((line) => !previous.some((old) => old.key === line.key));
    const removed = previous.find((line) => !lines.some((next) => next.key === line.key));
    const quantity = lines.find((line) => {
      const old = previous.find((candidate) => candidate.key === line.key);
      return old && old.quantity !== line.quantity;
    });
    const oldQuantity = quantity
      ? previous.find((line) => line.key === quantity.key)?.quantity
      : undefined;
    const cleared = Boolean(previous.length && !lines.length);
    if (!cleared && !added && !removed && !quantity) {
      return;
    }
    const operation = cleared
      ? "clear"
      : added
        ? "add"
        : removed
          ? "remove"
          : "quantity";
    diagnostics.record({
      name: "draft.changed",
      category: "USER_ACTION",
      outcome: "succeeded",
      actionId: diagnostics.newActionId(),
      entityType: added || removed || quantity ? "product" : tableId ? "table" : undefined,
      entityId: (added || removed || quantity)?.productId ?? tableId,
      metadata: {
        operation,
        table_id: tableId,
        product_id: (added || removed || quantity)?.productId,
        item_count: lines.length,
        quantity: quantity?.quantity,
        previous_quantity: oldQuantity
      }
    });
  }, []);

  /* Used when the waiter has asked for unsent lines to be thrown away on leaving a table. */
  const discardDraft = useCallback((tableLabel: string, actionId?: string) => {
    const discarded = draftsRef.current[tableLabel] ?? [];
    draftsRef.current = { ...draftsRef.current, [tableLabel]: [] };
    setDrafts((value) => {
      if (!value[tableLabel]?.length) {
        return value;
      }
      return { ...value, [tableLabel]: [] };
    });
    const current = sessionRef.current;
    if (current) {
      saveDraft(current, tableLabel, []);
    }
    if (discarded.length) {
      const tableId = setupRef.current?.shop_tables.find((table) => table.label === tableLabel)?.id;
      diagnostics.record({
        name: "draft.changed",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId: actionId ?? diagnostics.newActionId(),
        entityType: tableId ? "table" : undefined,
        entityId: tableId,
        metadata: { operation: "discard", table_id: tableId, item_count: discarded.length }
      });
    }
  }, []);

  const submitOrder = useCallback(async (
    tableLabel: string,
    lines: DraftLine[],
    printerIds: number[]
  ) => {
    if (!sessionRef.current || actionBusy) {
      return;
    }
    const existing = pendingForTable(pendingRef.current, ordersRef.current, tableLabel);
    // New products in hand mean a new order, whatever the previous submission is still waiting
    // for, so an unanswered printer cannot hold the table hostage. The one thing that still
    // takes priority is a submission whose order has not reached the server yet: retrying that
    // is idempotent, whereas a fresh submission alongside it could duplicate the order.
    const supersedesPending = Boolean(
      existing && lines.length && isOrderRecorded(existing, ordersRef.current)
    );
    const tableId = setupRef.current?.shop_tables.find((table) => table.label === tableLabel)?.id;
    if (existing && !supersedesPending) {
      const actionId = diagnostics.beginAction({
        name: "order.submit",
        category: "USER_ACTION",
        entityType: existing.orderId ? "order" : tableId ? "table" : undefined,
        entityId: existing.orderId || tableId,
        metadata: {
          operation: "retry",
          retry: true,
          submission_id: existing.submissionId,
          print_job_id: existing.printJobId,
          pending_state: existing.state,
          table_id: tableId
        }
      });
      await processSubmission(existing.submissionId, existing.state === "failed", actionId);
      return;
    }
    if (!lines.length) {
      return;
    }
    let resolvedRouting;
    try {
      resolvedRouting = resolveOrderPrintRouting(setupRef.current!, lines, printerIds);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t("Δεν ήταν δυνατή η δρομολόγηση εκτύπωσης."));
      return;
    }
    setActionBusy(true);
    const submissionId = uuid();
    const actionId = diagnostics.beginAction({
      name: "order.submit",
      category: "USER_ACTION",
      entityType: tableId ? "table" : undefined,
      entityId: tableId,
      metadata: {
        operation: "create",
        submission_id: submissionId,
        table_id: tableId,
        item_count: lines.length,
        printer_count: resolvedRouting.printerIds.length,
        retry: false
      }
    });
    const payload: Record<string, unknown> = {
      submission_id: submissionId,
      order_uuid: uuid(),
      response_mode: "delta",
      table_label: tableLabel,
      user_id: sessionRef.current.user.id,
      waiter: sessionRef.current.user.username,
      items: lines.map((line) => ({
        product_id: line.productId,
        name: line.name,
        print_name: line.printName,
        quantity: line.quantity,
        without_options: line.withoutOptions.join(", "),
        extras: selectedExtrasValue(
          setupRef.current?.products.find((product) => product.id === line.productId)?.extras ?? "",
          line.extras
        ),
        counters: counterText(line.counters),
        price: line.unitPrice,
        comment: line.comment.trim(),
        takeaway: line.takeaway
      })),
      table_changes: draftTableChangesRef.current
    };
    draftTableChangesRef.current = [];
    const item: PendingSubmission = {
      submissionId,
      actionId,
      printJobId: `order-${submissionId}`,
      tableLabel,
      payload,
      printerIds: resolvedRouting.printerIds,
      routeMode: resolvedRouting.routeMode,
      routing: resolvedRouting.routing,
      state: "order_pending",
      orderId: 0,
      eventId: 0,
      error: "",
      createdAt: Date.now()
    };
    persistPending([...pendingRef.current, item]);
    try {
      await processSubmission(submissionId, false, actionId);
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, persistPending, processSubmission]);

  const retryPending = useCallback(async (submissionId: string) => {
    const item = pendingRef.current.find((candidate) => candidate.submissionId === submissionId);
    if (!item || item.state === "printing" || actionBusy) {
      return;
    }
    setActionBusy(true);
    const actionId = diagnostics.beginAction({
      name: item.orderId ? "order.print" : "order.submit",
      category: "USER_ACTION",
      entityType: item.orderId ? "order" : undefined,
      entityId: item.orderId || undefined,
      metadata: {
        operation: "manual_retry",
        retry: true,
        submission_id: item.submissionId,
        print_job_id: item.printJobId,
        pending_state: item.state
      }
    });
    try {
      if (item.state === "failed") {
        updatePending(submissionId, { state: "print_pending", error: "" });
      }
      await processSubmission(submissionId, true, actionId);
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, processSubmission, updatePending]);

  /* Resolves true only when the table really closed, so the caller can leave the order builder
     on success and keep the waiter on it — with the error in view — when it failed. */
  const closeTable = useCallback(async (tableLabel: string): Promise<boolean> => {
    if (actionBusy) {
      return false;
    }
    setActionBusy(true);
    const tableId = setupRef.current?.shop_tables.find((table) => table.label === tableLabel)?.id;
    const actionId = diagnostics.beginAction({
      name: "order.mutation",
      category: "USER_ACTION",
      entityType: tableId ? "table" : undefined,
      entityId: tableId,
      metadata: { operation: "close_table", table_id: tableId }
    });
    try {
      const response = await apiRef.current.closeTable(tableLabel, actionId);
      persistOrders(response.orders);
      persistPending(pendingRef.current.filter(
        (item) => pendingTableLabel(item, response.orders) !== tableLabel
      ));
      setMessage(t("Το τραπέζι {0} έκλεισε.", tableLabel));
      // Closing retires an ad-hoc table server-side, so take the fresh setup rather than leave
      // the grid showing a table that no longer exists until the next poll.
      try {
        acceptServerSetup(await apiRef.current.setup(actionId));
      } catch {
        // The table list catches up on the next sync; the close itself already succeeded.
      }
      diagnostics.record({
        name: "order.mutation",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        entityType: tableId ? "table" : undefined,
        entityId: tableId,
        metadata: { operation: "close_table", table_id: tableId, changed_count: response.changed }
      });
      return true;
    } catch (error) {
      diagnostics.record({
        name: "order.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        entityType: tableId ? "table" : undefined,
        entityId: tableId,
        metadata: { operation: "close_table", table_id: tableId, ...safeDiagnosticError(error) }
      });
      setMessage(error instanceof Error ? error.message : t("Δεν έκλεισε το τραπέζι."));
      return false;
    } finally {
      setActionBusy(false);
    }
  }, [acceptServerSetup, actionBusy, persistOrders, persistPending]);

  const setServed = useCallback(async (lineIds: number[], served: boolean) => {
    const actionId = diagnostics.beginAction({
      name: "order.mutation",
      category: "USER_ACTION",
      entityType: lineIds.length === 1 ? "order_item" : undefined,
      entityId: lineIds.length === 1 ? lineIds[0] : undefined,
      metadata: {
        operation: "set_served",
        line_id: lineIds.length === 1 ? lineIds[0] : undefined,
        item_count: lineIds.length,
        served
      }
    });
    try {
      const response = await apiRef.current.setServed(lineIds, served, actionId);
      persistOrders(response.orders);
      diagnostics.record({
        name: "order.mutation",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        entityType: lineIds.length === 1 ? "order_item" : undefined,
        entityId: lineIds.length === 1 ? lineIds[0] : undefined,
        metadata: {
          operation: "set_served",
          line_id: lineIds.length === 1 ? lineIds[0] : undefined,
          item_count: lineIds.length,
          served,
          changed_count: response.changed
        }
      });
    } catch (error) {
      diagnostics.record({
        name: "order.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        metadata: {
          operation: "set_served",
          item_count: lineIds.length,
          served,
          ...safeDiagnosticError(error)
        }
      });
      setMessage(error instanceof Error ? error.message : t("Δεν ενημερώθηκαν τα προϊόντα."));
    }
  }, [persistOrders]);

  /* Spawns the table behind "Δημιουργία παραγγελίας". The server owns the placement and the
     temporary flag, so the fresh setup it returns is accepted wholesale rather than guessed at
     locally — and unlike a setup patch, a waiter is allowed to do this. */
  const createTemporaryTable = useCallback(async (label: string): Promise<boolean> => {
    const clean = label.trim();
    if (!clean || !sessionRef.current) {
      return false;
    }
    const current = setupRef.current;
    if (current?.shop_tables.some((table) => table.active && table.label === clean)) {
      return true;
    }
    const pageId = current?.shop_tables.find((table) => table.active)?.page_id ?? 0;
    const actionId = diagnostics.beginAction({
      name: "table.mutation",
      category: "USER_ACTION",
      metadata: { operation: "temporary_create", page_id: pageId }
    });
    try {
      const response = await apiRef.current.createTemporaryTable(clean, pageId, actionId);
      acceptServerSetup(await apiRef.current.setup(actionId));
      diagnostics.record({
        name: "table.mutation",
        category: "USER_ACTION",
        outcome: response.created ? "succeeded" : "deduplicated",
        actionId,
        entityType: "table",
        entityId: response.table_id,
        metadata: { operation: "temporary_create", page_id: pageId, table_id: response.table_id }
      });
      return true;
    } catch (error) {
      diagnostics.record({
        name: "table.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        metadata: { operation: "temporary_create", page_id: pageId, ...safeDiagnosticError(error) }
      });
      setMessage(error instanceof Error ? error.message : t("Δεν δημιουργήθηκε το τραπέζι."));
      return false;
    }
  }, [acceptServerSetup]);

  const moveTableLayout = useCallback(async (
    tableId: number,
    targetRow: number,
    targetCol: number
  ) => {
    const current = setupRef.current;
    if (!current) {
      return;
    }
    const moving = current.shop_tables.find((table) => table.id === tableId);
    if (!moving || (moving.grid_row === targetRow && moving.grid_col === targetCol)) {
      return;
    }
    const occupant = current.shop_tables.find((table) =>
      table.active &&
      table.page_id === moving.page_id &&
      table.grid_row === targetRow &&
      table.grid_col === targetCol
    );
    persistSetup({
      ...current,
      shop_tables: current.shop_tables.map((table) => {
        if (table.id === moving.id) {
          return { ...table, grid_row: targetRow, grid_col: targetCol };
        }
        if (occupant && table.id === occupant.id) {
          return { ...table, grid_row: moving.grid_row, grid_col: moving.grid_col };
        }
        return table;
      })
    });
    const actionId = diagnostics.beginAction({
      name: "table.mutation",
      category: "USER_ACTION",
      entityType: "table",
      entityId: tableId,
      metadata: { operation: "layout_move", table_id: tableId, page_id: moving.page_id }
    });
    try {
      const response = await apiRef.current.moveTableLayout(tableId, targetRow, targetCol, actionId);
      const positions = new Map(response.tables.map((table) => [table.id, table]));
      const applyLayout = (base: Setup): Setup => ({
        ...base,
        revision: response.setup_revision,
        table_layout_revision: response.revision,
        shop_tables: base.shop_tables.map((table) => {
          const position = positions.get(table.id);
          return position ? { ...table, ...position } : table;
        })
      });
      if (committedSetupRef.current) {
        committedSetupRef.current = applyLayout(committedSetupRef.current);
      }
      if (setupRef.current) {
        persistSetup(applyLayout(setupRef.current));
      }
      diagnostics.record({
        name: "table.mutation",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        entityType: "table",
        entityId: tableId,
        metadata: { operation: "layout_move", table_id: tableId, page_id: moving.page_id }
      });
    } catch (error) {
      try {
        acceptServerSetup(await apiRef.current.setup(actionId));
      } catch {
        // The optimistic layout remains visible until connectivity returns.
      }
      diagnostics.record({
        name: "table.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        entityType: "table",
        entityId: tableId,
        metadata: { operation: "layout_move", table_id: tableId, ...safeDiagnosticError(error) }
      });
      setMessage(error instanceof Error ? error.message : t("Δεν άλλαξε η θέση του τραπεζιού."));
    }
  }, [acceptServerSetup, persistSetup]);

  const changePassword = useCallback(async (newPassword: string) => {
    const actionId = diagnostics.beginAction({
      name: "account.password_change",
      category: "USER_ACTION",
      metadata: { operation: "change" }
    });
    try {
      await apiRef.current.changePassword(newPassword, actionId);
      diagnostics.record({
        name: "account.password_change",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        metadata: { operation: "change" }
      });
      setMessage(t("Ο κωδικός πρόσβασης άλλαξε."));
    } catch (error) {
      diagnostics.record({
        name: "account.password_change",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        metadata: { operation: "change", ...safeDiagnosticError(error) }
      });
      throw error;
    }
  }, []);

  const disableMfa = useCallback(async () => {
    const actionId = diagnostics.beginAction({
      name: "auth.session",
      category: "AUTH",
      metadata: { operation: "disable_mfa" }
    });
    await diagnostics.flush();
    await apiRef.current.disableMfa(actionId);
    diagnostics.record({
      name: "auth.session",
      category: "AUTH",
      outcome: "succeeded",
      actionId,
      metadata: { operation: "disable_mfa" }
    });
    invalidateSession();
    setMessage("");
  }, [invalidateSession]);

  const enableMfa = useCallback(async () => {
    const current = sessionRef.current;
    if (!current) {
      throw new Error(t("Δεν υπάρχει ενεργή σύνδεση."));
    }
    const actionId = diagnostics.beginAction({
      name: "auth.session",
      category: "AUTH",
      metadata: { operation: "enable_mfa" }
    });
    setLoginBusy(true);
    setMessage("");
    try {
      const response = await apiRef.current.beginMfaSetup(actionId);
      setMfaRecoveryCodes([]);
      setMfaChallenge({
        ...response,
        businessSlug: current.businessSlug,
        remember: rememberRef.current,
        actionId
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t("Δεν ξεκίνησε η ρύθμιση 2FA."));
      throw error;
    } finally {
      setLoginBusy(false);
    }
  }, []);

  const pushSetup = useCallback((next: Setup): Promise<void> => {
    if (!committedSetupRef.current) {
      return Promise.resolve();
    }
    const previewPatch = setupPatchBetween(committedSetupRef.current, next);
    const upsertCount = Object.values(previewPatch.upserts)
      .reduce((total, rows) => total + (rows?.length ?? 0), 0);
    const deleteCount = Object.values(previewPatch.deletes)
      .reduce((total, rows) => total + (rows?.length ?? 0), 0);
    if (!upsertCount && !deleteCount) {
      return Promise.resolve();
    }
    const actionId = diagnostics.beginAction({
      name: "setup.patch",
      category: "USER_ACTION",
      entityType: "setup",
      entityId: "current",
      metadata: { operation: "save", upsert_count: upsertCount, delete_count: deleteCount }
    });
    persistSetup(next);
    const generation = ++setupMutationGenerationRef.current;
    setupMutationPendingRef.current += 1;

    const operation = setupMutationQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        let base = committedSetupRef.current;
        const desired = setupRef.current;
        if (!base || !desired) {
          return;
        }
        let patch = setupPatchBetween(base, desired);
        if (!hasSetupPatchChanges(patch)) {
          return;
        }
        try {
          const response = await apiRef.current.patchSetup(patch, actionId);
          committedSetupRef.current = response.setup;
          if (generation === setupMutationGenerationRef.current) {
            acceptServerSetup(response.setup);
          }
        } catch (firstError) {
          const fresh = await apiRef.current.setup(actionId);
          committedSetupRef.current = fresh;
          const latestDesired = setupRef.current;
          if (!latestDesired) {
            throw firstError;
          }
          patch = setupPatchBetween(fresh, latestDesired);
          if (!hasSetupPatchChanges(patch)) {
            if (generation === setupMutationGenerationRef.current) {
              acceptServerSetup(fresh);
            }
            return;
          }
          const response = await apiRef.current.patchSetup(patch, actionId);
          committedSetupRef.current = response.setup;
          if (generation === setupMutationGenerationRef.current) {
            acceptServerSetup(response.setup);
          }
        }
      });

    setupMutationQueueRef.current = operation.then(() => undefined, () => undefined);
    return operation
      .then(() => {
        diagnostics.record({
          name: "setup.patch",
          category: "USER_ACTION",
          outcome: "succeeded",
          actionId,
          entityType: "setup",
          entityId: "current",
          metadata: { operation: "save", upsert_count: upsertCount, delete_count: deleteCount }
        });
        if (generation === setupMutationGenerationRef.current) {
          setMessage(t("Οι αλλαγές αποθηκεύτηκαν."));
        }
      })
      .catch(async (error) => {
        diagnostics.record({
          name: "setup.patch",
          category: "USER_ACTION",
          severity: "ERROR",
          outcome: navigator.onLine ? "failed" : "offline",
          actionId,
          entityType: "setup",
          entityId: "current",
          metadata: {
            operation: "save",
            upsert_count: upsertCount,
            delete_count: deleteCount,
            ...safeDiagnosticError(error)
          }
        });
        if (generation === setupMutationGenerationRef.current) {
          try {
            acceptServerSetup(await apiRef.current.setup(actionId));
          } catch {
            // Keep the optimistic setup available while offline.
          }
          setMessage(error instanceof Error ? error.message : t("Δεν αποθηκεύτηκαν οι αλλαγές."));
        }
        if (previewPatch.upserts.users?.length || previewPatch.deletes.users?.length) {
          // Credentials are never queued offline or retained in a phantom cached account.
          if (committedSetupRef.current && generation === setupMutationGenerationRef.current) {
            acceptServerSetup(committedSetupRef.current);
          }
          throw error;
        }
      })
      .finally(() => {
        setupMutationPendingRef.current = Math.max(0, setupMutationPendingRef.current - 1);
      });
  }, [acceptServerSetup, persistSetup]);

  const mergeTables = useCallback(async (label: string, tableIds: number[]) => {
    if (actionBusy) {
      return;
    }
    setActionBusy(true);
    const actionId = diagnostics.beginAction({
      name: "table.mutation",
      category: "USER_ACTION",
      metadata: { operation: "merge", item_count: tableIds.length }
    });
    try {
      await apiRef.current.mergeTables(label, tableIds, actionId);
      const fresh = await apiRef.current.orders(actionId);
      persistOrders(fresh);
      setMessage(t("Τα τραπέζια συνενώθηκαν σε \"{0}\".", label));
      diagnostics.record({
        name: "table.mutation",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        metadata: { operation: "merge", item_count: tableIds.length }
      });
    } catch (error) {
      diagnostics.record({
        name: "table.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        metadata: { operation: "merge", item_count: tableIds.length, ...safeDiagnosticError(error) }
      });
      setMessage(error instanceof Error ? error.message : t("Δεν έγινε η συνένωση."));
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, persistOrders]);

  const extendMerge = useCallback(async (mergeId: number, label: string, tableIds: number[]) => {
    if (actionBusy) {
      return;
    }
    setActionBusy(true);
    const metadata = { operation: "extend", merge_id: mergeId, item_count: tableIds.length };
    const actionId = diagnostics.beginAction({
      name: "table.mutation",
      category: "USER_ACTION",
      entityType: "table",
      entityId: mergeId,
      metadata
    });
    try {
      await apiRef.current.extendMerge(mergeId, label, tableIds, actionId);
      const fresh = await apiRef.current.orders(actionId);
      persistOrders(fresh);
      setMessage(t("Τα τραπέζια προστέθηκαν στο \"{0}\".", label));
      diagnostics.record({
        name: "table.mutation",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        entityType: "table",
        entityId: mergeId,
        metadata
      });
    } catch (error) {
      diagnostics.record({
        name: "table.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        entityType: "table",
        entityId: mergeId,
        metadata: { ...metadata, ...safeDiagnosticError(error) }
      });
      setMessage(error instanceof Error ? error.message : t("Δεν έγινε η προσθήκη τραπεζιών."));
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, persistOrders]);

  const splitTable = useCallback(async (mergeId: number) => {
    if (actionBusy) {
      return;
    }
    setActionBusy(true);
    const actionId = diagnostics.beginAction({
      name: "table.mutation",
      category: "USER_ACTION",
      entityType: "table",
      entityId: mergeId,
      metadata: { operation: "split", merge_id: mergeId }
    });
    try {
      await apiRef.current.splitTable(mergeId, actionId);
      const fresh = await apiRef.current.orders(actionId);
      persistOrders(fresh);
      setMessage(t("Τα τραπέζια διαχωρίστηκαν."));
      diagnostics.record({
        name: "table.mutation",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        entityType: "table",
        entityId: mergeId,
        metadata: { operation: "split", merge_id: mergeId }
      });
    } catch (error) {
      diagnostics.record({
        name: "table.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        entityType: "table",
        entityId: mergeId,
        metadata: { operation: "split", merge_id: mergeId, ...safeDiagnosticError(error) }
      });
      setMessage(error instanceof Error ? error.message : t("Δεν έγινε ο διαχωρισμός."));
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, persistOrders]);

  const cancelOrder = useCallback(async (orderId: number, note = "") => {
    if (actionBusy) {
      return;
    }
    setActionBusy(true);
    const actionId = diagnostics.beginAction({
      name: "order.mutation",
      category: "USER_ACTION",
      entityType: "order",
      entityId: orderId,
      metadata: { operation: "cancel", order_id: orderId }
    });
    const submissionId = uuid();
    try {
      const waiter = sessionRef.current?.user.username ?? "";
      const response = await apiRef.current.cancelOrder(
        orderId,
        waiter,
        submissionId,
        note,
        actionId
      );
      persistOrders(response.orders);
      setMessage(t("Η παραγγελία ακυρώθηκε."));
      diagnostics.record({
        name: "order.mutation",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        entityType: "order",
        entityId: orderId,
        metadata: { operation: "cancel", order_id: orderId, submission_id: submissionId }
      });
    } catch (error) {
      diagnostics.record({
        name: "order.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        entityType: "order",
        entityId: orderId,
        metadata: {
          operation: "cancel",
          order_id: orderId,
          submission_id: submissionId,
          ...safeDiagnosticError(error)
        }
      });
      setMessage(error instanceof Error ? error.message : t("Δεν ακυρώθηκε η παραγγελία."));
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, persistOrders]);

  const correctProduct = useCallback(async (input: {
    orderId: number;
    lineId: number;
    targetQuantity: number;
    withoutOptions: string;
    extras: string;
    counters: string;
    itemComment: string;
    takeaway: boolean;
    price: number;
  }) => {
    if (actionBusy) {
      return;
    }
    setActionBusy(true);
    const actionId = diagnostics.beginAction({
      name: "order.mutation",
      category: "USER_ACTION",
      entityType: "order_item",
      entityId: input.lineId,
      metadata: {
        operation: "correct",
        order_id: input.orderId,
        line_id: input.lineId,
        target_quantity: input.targetQuantity
      }
    });
    const submissionId = uuid();
    try {
      const waiter = sessionRef.current?.user.username ?? "";
      const response = await apiRef.current.correctProduct({
        ...input,
        waiter,
        submissionId
      }, actionId);
      persistOrders(response.orders);
      setMessage(t("Η διόρθωση καταχωρήθηκε."));
      diagnostics.record({
        name: "order.mutation",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        entityType: "order_item",
        entityId: input.lineId,
        metadata: {
          operation: "correct",
          order_id: input.orderId,
          line_id: input.lineId,
          target_quantity: input.targetQuantity,
          submission_id: submissionId
        }
      });
    } catch (error) {
      diagnostics.record({
        name: "order.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        entityType: "order_item",
        entityId: input.lineId,
        metadata: {
          operation: "correct",
          order_id: input.orderId,
          line_id: input.lineId,
          target_quantity: input.targetQuantity,
          submission_id: submissionId,
          ...safeDiagnosticError(error)
        }
      });
      setMessage(error instanceof Error ? error.message : t("Δεν έγινε η διόρθωση."));
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, persistOrders]);

  /* Moves a table's unsent products to another table, as beginDraftTableChange() does on
     Android. There is no order to move yet, so this is local: the draft is rewritten under the
     new table and the move is queued so the eventual order still records where it came from. */
  const moveDraftToTable = useCallback((sourceLabel: string, targetLabel: string): boolean => {
    const current = sessionRef.current;
    if (!current || sourceLabel === targetLabel) {
      return false;
    }
    const lines = draftsRef.current[sourceLabel] ?? [];
    draftTableChangesRef.current = [
      ...draftTableChangesRef.current,
      { source_table_label: sourceLabel, target_table_label: targetLabel, created_at: Date.now() }
    ];
    setDraft(targetLabel, lines);
    setDraft(sourceLabel, []);
    diagnostics.record({
      name: "order.mutation",
      category: "USER_ACTION",
      outcome: "succeeded",
      actionId: diagnostics.newActionId(),
      metadata: { operation: "table_change" }
    });
    setMessage(t("Το τραπέζι άλλαξε σε {0}.", targetLabel));
    return true;
  }, [setDraft]);

  /* Prints the move to whichever printers this business has configured for it. */
  const printTableChange = useCallback(async (eventId: number, orderId: number) => {
    if (!eventId) {
      return;
    }
    try {
      const printerIds = tableChangePrinters(setupRef.current);
      if (printerIds.length === 0) {
        return;
      }
      const payloads = await buildOrderPayloads(setupRef.current, ordersRef.current, {
        orderId,
        eventId,
        profile: "order",
        printerIds
      });
      if (!payloads) {
        return;
      }
      await apiRef.current.rawPrint({
        // Keyed on the event, so a repeat never puts a second slip on the rail.
        printJobId: `table-${eventId}`,
        orderId,
        eventId,
        printerPayloads: payloads
      });
    } catch (error) {
      diagnostics.record({
        name: "order.print",
        category: "USER_ACTION",
        severity: "WARNING",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId: diagnostics.newActionId(),
        entityType: "order",
        entityId: orderId,
        metadata: { operation: "table_change", ...safeDiagnosticError(error) }
      });
    }
  }, []);

  const changeOrderTable = useCallback(async (
    orderId: number,
    sourceTableLabel: string,
    targetTableLabel: string
  ) => {
    if (actionBusy) {
      return false;
    }
    setActionBusy(true);
    const targetTableId = setupRef.current?.shop_tables
      .find((table) => table.label === targetTableLabel)?.id;
    const actionId = diagnostics.beginAction({
      name: "order.mutation",
      category: "USER_ACTION",
      entityType: "order",
      entityId: orderId,
      metadata: { operation: "transfer", order_id: orderId, table_id: targetTableId }
    });
    const submissionId = uuid();
    try {
      const waiter = sessionRef.current?.user.username ?? "";
      const response = await apiRef.current.changeOrderTable({
        orderId,
        sourceTableLabel,
        targetTableLabel,
        waiter,
        submissionId
      }, actionId);
      persistOrders(response.orders);
      setMessage(t("Η παραγγελία μεταφέρθηκε στο τραπέζι {0}.", targetTableLabel));
      /* Tell the printers the order moved. A kitchen holding a ticket marked with the old table
         has no other way to learn about it. Deliberately not awaited and never allowed to throw:
         the move itself has already succeeded, and a printer being off must not report that as a
         failed transfer. */
      void printTableChange(response.event_id, orderId);
      diagnostics.record({
        name: "order.mutation",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        entityType: "order",
        entityId: orderId,
        metadata: {
          operation: "transfer",
          order_id: orderId,
          table_id: targetTableId,
          submission_id: submissionId
        }
      });
      return true;
    } catch (error) {
      diagnostics.record({
        name: "order.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        entityType: "order",
        entityId: orderId,
        metadata: {
          operation: "transfer",
          order_id: orderId,
          table_id: targetTableId,
          submission_id: submissionId,
          ...safeDiagnosticError(error)
        }
      });
      setMessage(error instanceof Error ? error.message : t("Δεν άλλαξε το τραπέζι."));
      return false;
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, persistOrders]);

  const reprintOrder = useCallback(async (order: OrderRecord, printerIds: number[]) => {
    if (historyPrintBusyRef.current.has(order.id)) {
      return;
    }
    historyPrintBusyRef.current.add(order.id);
    setActionBusy(true);
    const printJobId = `history-${order.id}-${uuid()}`;
    const actionId = diagnostics.beginAction({
      name: "history.mutation",
      category: "USER_ACTION",
      entityType: "order",
      entityId: order.id,
      metadata: {
        operation: "reprint",
        order_id: order.id,
        print_job_id: printJobId,
        printer_count: printerIds.length
      }
    });
    try {
      let response = (await queuePrintJob({
        printJobId,
        orderId: order.id,
        eventId: 0,
        profile: "history",
        printerIds
      }, actionId)).response;
      let status = response.job.status;
      if (response.client_delivery_required && !["sent", "sent_direct", "failed", "unknown"].includes(status)) {
        setMessage(t("Η επανεκτύπωση μπήκε στην ουρά."));
        for (let attempt = 0; attempt < 50; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 600));
          const result = await apiRef.current.printStatus(printJobId, actionId);
          if (result.job) {
            response = { ...response, job: result.job };
            status = result.job.status;
          }
          if (["sent", "sent_direct", "failed", "unknown"].includes(status)) {
            break;
          }
        }
      }
      if (["sent", "sent_direct"].includes(status)) {
        persistPending(pendingRef.current.filter((item) => item.orderId !== order.id));
        setMessage(t("Η επανεκτύπωση ολοκληρώθηκε."));
        diagnostics.record({
          name: "history.mutation",
          category: "USER_ACTION",
          outcome: response.deduplicated ? "deduplicated" : "succeeded",
          actionId,
          entityType: "order",
          entityId: order.id,
          metadata: {
            operation: "reprint",
            order_id: order.id,
            print_job_id: printJobId,
            result: status,
            deduplicated: response.deduplicated
          }
        });
      } else if (status === "failed") {
        throw new Error(response.job.last_error || t("Η επανεκτύπωση απέτυχε."));
      } else if (status === "unknown") {
        throw new Error(response.job.last_error || t("Το αποτέλεσμα της επανεκτύπωσης είναι άγνωστο."));
      } else {
        throw new Error(t("Η επανεκτύπωση δεν επιβεβαιώθηκε εγκαίρως."));
      }
    } catch (error) {
      diagnostics.record({
        name: "history.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        entityType: "order",
        entityId: order.id,
        metadata: {
          operation: "reprint",
          order_id: order.id,
          print_job_id: printJobId,
          ...safeDiagnosticError(error)
        }
      });
      setMessage(error instanceof Error ? error.message : t("Η επανεκτύπωση απέτυχε."));
    } finally {
      historyPrintBusyRef.current.delete(order.id);
      setActionBusy(false);
    }
  }, [persistPending]);

  const clearHistory = useCallback(async () => {
    if (actionBusy) {
      return;
    }
    setActionBusy(true);
    const actionId = diagnostics.beginAction({
      name: "history.mutation",
      category: "USER_ACTION",
      metadata: { operation: "clear" }
    });
    try {
      const response = await apiRef.current.clearOrders(actionId);
      persistOrders(response.orders, false);
      persistPending([]);
      setMessage(t("Το ιστορικό διαγράφηκε."));
      diagnostics.record({
        name: "history.mutation",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        metadata: { operation: "clear" }
      });
    } catch (error) {
      diagnostics.record({
        name: "history.mutation",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        metadata: { operation: "clear", ...safeDiagnosticError(error) }
      });
      setMessage(error instanceof Error ? error.message : t("Δεν διαγράφηκε το ιστορικό."));
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, persistOrders, persistPending]);

  const loadSalesPeriodReport = useCallback(async (): Promise<SalesPeriodReport> => {
    return apiRef.current.currentSalesPeriodReport(diagnostics.newActionId());
  }, []);

  const printSalesPeriodReport = useCallback(async (
    report: SalesPeriodReport,
    printerId: number
  ): Promise<void> => {
    if (actionBusy || !setupRef.current) return;
    setActionBusy(true);
    const actionId = diagnostics.beginAction({
      name: "history.mutation",
      category: "USER_ACTION",
      entityType: "printer",
      entityId: printerId,
      metadata: { operation: "print_current_period" }
    });
    try {
      const payloads = await buildSalesReportPayloads(setupRef.current, report, printerId);
      const response = await apiRef.current.rawPrint({
        printJobId: `period-report-${uuid()}`,
        orderId: 0,
        eventId: 0,
        printerPayloads: payloads,
        routeMode: "manual"
      }, actionId);
      setMessage(response.client_delivery_required
        ? t("Η αναφορά μπήκε στην ουρά εκτύπωσης.")
        : t("Η αναφορά εκτυπώθηκε."));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t("Η αναφορά δεν εκτυπώθηκε."));
      throw error;
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy]);

  const testPrint = useCallback(async (printerId: number) => {
    if (actionBusy) {
      return;
    }
    setActionBusy(true);
    const actionId = diagnostics.beginAction({
      name: "printer.check",
      category: "USER_ACTION",
      entityType: "printer",
      entityId: printerId,
      metadata: { operation: "test_print" }
    });
    try {
      // Render the test ticket here so it shows this shop's real products, prices and category
      // order. Only if the browser cannot render does it fall back to the server's plain payload.
      const testPayloads = await buildTestPayloads(
        setupRef.current,
        printerId,
        sessionRef.current?.user.username ?? "admin"
      );
      const response = testPayloads
        ? await apiRef.current.rawPrint({
            printJobId: `web-test-${uuid()}`,
            orderId: 0,
            eventId: 0,
            clientProfile: "test",
            printerPayloads: testPayloads
          }, actionId)
        : await apiRef.current.testPrint(printerId, actionId);
      let status = response.job.status;
      if (response.client_delivery_required) {
        setMessage(t("Η δοκιμαστική εκτύπωση μπήκε στην ουρά."));
        for (let attempt = 0; attempt < 50; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 600));
          const result = await apiRef.current.printStatus(response.job.job_id, actionId);
          status = result.job?.status ?? status;
          if (["sent", "sent_direct", "failed", "unknown"].includes(status)) {
            if (status === "failed" || status === "unknown") {
              throw new Error(result.job?.last_error || t("Η δοκιμαστική εκτύπωση απέτυχε."));
            }
            break;
          }
        }
      }
      if (["sent", "sent_direct"].includes(status)) {
        setMessage(t("Η δοκιμαστική εκτύπωση ολοκληρώθηκε."));
        diagnostics.record({
          name: "printer.check",
          category: "USER_ACTION",
          outcome: "succeeded",
          actionId,
          entityType: "printer",
          entityId: printerId,
          metadata: { operation: "test_print", print_job_id: response.job.job_id, result: status }
        });
      } else {
        throw new Error(t("Η δοκιμαστική εκτύπωση δεν επιβεβαιώθηκε εγκαίρως."));
      }
    } catch (error) {
      diagnostics.record({
        name: "printer.check",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        entityType: "printer",
        entityId: printerId,
        metadata: { operation: "test_print", ...safeDiagnosticError(error) }
      });
      setMessage(error instanceof Error ? error.message : t("Η δοκιμαστική εκτύπωση απέτυχε."));
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy]);

  const probePrinter = useCallback(async (printerId: number) => {
    if (actionBusy) {
      return;
    }
    setActionBusy(true);
    const actionId = diagnostics.beginAction({
      name: "printer.check",
      category: "USER_ACTION",
      entityType: "printer",
      entityId: printerId,
      metadata: { operation: "probe" }
    });
    try {
      const response = await apiRef.current.probePrinter(printerId, actionId);
      let status = response.job.status;
      setMessage(t("Έλεγχος σύνδεσης εκτυπωτή..."));
      for (let attempt = 0; response.client_delivery_required && attempt < 35; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 500));
        const result = await apiRef.current.printStatus(response.job.job_id, actionId);
        status = result.job?.status ?? status;
        if (["sent", "sent_direct", "failed", "unknown"].includes(status)) {
          if (status === "failed" || status === "unknown") {
            throw new Error(result.job?.last_error || t("Ο εκτυπωτής δεν απάντησε."));
          }
          break;
        }
      }
      if (!["sent", "sent_direct"].includes(status)) {
        throw new Error(t("Ο έλεγχος σύνδεσης δεν επιβεβαιώθηκε εγκαίρως."));
      }
      setMessage(t("Η σύνδεση με τον εκτυπωτή λειτουργεί. Δεν καταναλώθηκε χαρτί."));
      diagnostics.record({
        name: "printer.check",
        category: "USER_ACTION",
        outcome: "succeeded",
        actionId,
        entityType: "printer",
        entityId: printerId,
        metadata: { operation: "probe", print_job_id: response.job.job_id, result: status }
      });
    } catch (error) {
      diagnostics.record({
        name: "printer.check",
        category: "USER_ACTION",
        severity: "ERROR",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId,
        entityType: "printer",
        entityId: printerId,
        metadata: { operation: "probe", ...safeDiagnosticError(error) }
      });
      setMessage(error instanceof Error ? error.message : t("Ο έλεγχος σύνδεσης απέτυχε."));
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy]);

  const requestNotifications = useCallback(async () => {
    if (!("Notification" in window)) {
      diagnostics.record({
        name: "app.lifecycle",
        category: "UI",
        severity: "WARNING",
        outcome: "failed",
        actionId: diagnostics.newActionId(),
        metadata: {
          operation: "notification_permission",
          result: "unsupported"
        }
      });
      setMessage(t("Αυτή η συσκευή δεν υποστηρίζει ειδοποιήσεις web."));
      return "denied" as NotificationPermission;
    }
    const actionId = diagnostics.newActionId();
    const permission = await Notification.requestPermission();
    diagnostics.record({
      name: "app.lifecycle",
      category: "USER_ACTION",
      severity: permission === "denied" ? "WARNING" : "INFO",
      outcome: permission === "granted" ? "succeeded" : "cancelled",
      actionId,
      metadata: {
        operation: "notification_permission",
        permission,
        result: permission
      }
    });
    if (permission === "granted") {
      playOrderSound();
      setMessage(t("Οι ειδοποιήσεις ενεργοποιήθηκαν."));
    } else {
      setMessage(t("Οι ειδοποιήσεις δεν επιτράπηκαν."));
    }
    return permission;
  }, []);

  const loadGatewayStatus = useCallback(() => apiRef.current.printGatewayStatus(), []);

  const enabledPrinters: PrinterConfig[] = useMemo(
    () => setup?.printers
      .filter((printer) => printer.enabled)
      .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id) ?? [],
    [setup]
  );

  return {
    session,
    setup,
    orders,
    pending,
    drafts,
    connection,
    syncing,
    lastSyncAt,
    message,
    setMessage,
    loginBusy,
    mfaChallenge,
    mfaRecoveryCodes,
    actionBusy,
    health,
    enabledPrinters,
    login,
    completeMfa,
    cancelMfa,
    acknowledgeRecoveryCodes,
    logout,
    sync,
    ensureDraft,
    setDraft,
    discardDraft,
    submitOrder,
    retryPending,
    closeTable,
    setServed,
    saveSetup: pushSetup,
    moveTableLayout,
    createTemporaryTable,
    changePassword,
    enableMfa,
    disableMfa,
    mergeTables,
    extendMerge,
    splitTable,
    cancelOrder,
    correctProduct,
    changeOrderTable,
    moveDraftToTable,
    reprintOrder,
    clearHistory,
    loadSalesPeriodReport,
    loadGatewayStatus,
    printSalesPeriodReport,
    testPrint,
    probePrinter,
    requestNotifications
  };
}
